/// Three-point fix — the classic "cocked hat" position-finding
/// method used at Day Skipper level.
///
/// You take a compass bearing to three identifiable charted objects,
/// convert each to a true bearing, and plot the RECIPROCAL of each
/// from its object. The three plotted lines almost never meet at a
/// single point; the small triangle they form is called the "cocked
/// hat", and your position lies somewhere inside it (or, on a poor
/// fix, just outside it). The smaller the cocked hat, the more
/// reliable the fix.
///
/// All angles here are in degrees, positions in decimal degrees of
/// latitude + longitude. We use a flat-earth approximation
/// (Mercator small-area projection): each degree of latitude is 60
/// nautical miles; each degree of longitude is 60·cos(lat) nautical
/// miles. Good to a few metres over the ranges that pilotage fixes
/// actually involve (typically < 10 nm to each mark).

export interface Mark {
  /// Display label — used by the figure component and the reveal.
  label: string;
  /// Latitude of the charted object (degrees, N positive).
  latDeg: number;
  /// Longitude of the charted object (degrees, E positive).
  lonDeg: number;
}

export interface Bearing {
  mark: Mark;
  /// True bearing FROM the observer TO the mark (degrees, 0..360).
  /// To get this from a compass bearing, apply your variation +
  /// deviation correction first.
  trueDeg: number;
}

export interface CockedHat {
  /// The three pairwise-intersection vertices of the position lines,
  /// in the order (12, 23, 31).
  vertices: readonly { latDeg: number; lonDeg: number }[];
  /// The mean (centroid) of the three vertices — the conventional
  /// "best estimate" of position.
  centroid: { latDeg: number; lonDeg: number };
  /// Mean side length of the triangle, in nautical miles. < 0.1 nm
  /// is excellent, 0.1–0.3 acceptable, > 0.3 means re-check the
  /// bearings or pick different marks.
  meanSideNm: number;
  /// True if every pair of lines intersects at a real point. Very
  /// rare to hit the parallel-lines edge case in practice.
  valid: boolean;
}

/// Pairwise intersection of three position lines, plus the centroid.
/// Each bearing produces a position line drawn from the mark in the
/// reciprocal direction (since the observer is on the opposite side
/// of the bearing from the mark).
export function cockedHat(bearings: readonly Bearing[]): CockedHat {
  if (bearings.length !== 3) {
    throw new Error("cockedHat requires exactly three bearings");
  }
  // Reference latitude for the equirectangular projection — the
  // average of the three marks is good enough at pilotage scales.
  const refLat =
    (bearings[0]!.mark.latDeg +
      bearings[1]!.mark.latDeg +
      bearings[2]!.mark.latDeg) /
    3;
  const cosRef = Math.cos((refLat * Math.PI) / 180);

  // Convert each mark + reciprocal-bearing pair into a 2D point +
  // direction vector in nautical-mile coordinates (x = east, y = north).
  const lines = bearings.map((b) => {
    const reciprocalDeg = (b.trueDeg + 180) % 360;
    const rad = (reciprocalDeg * Math.PI) / 180;
    return {
      x: (b.mark.lonDeg - bearings[0]!.mark.lonDeg) * 60 * cosRef,
      y: (b.mark.latDeg - bearings[0]!.mark.latDeg) * 60,
      // (east, north) direction the position line runs in.
      dx: Math.sin(rad),
      dy: Math.cos(rad),
    };
  });

  const intersect = (
    a: (typeof lines)[number],
    b: (typeof lines)[number],
  ): { x: number; y: number } | null => {
    // Solve a.point + t * a.dir = b.point + s * b.dir
    const det = a.dx * -b.dy - a.dy * -b.dx;
    if (Math.abs(det) < 1e-9) return null;
    const t = ((b.x - a.x) * -b.dy - (b.y - a.y) * -b.dx) / det;
    return { x: a.x + t * a.dx, y: a.y + t * a.dy };
  };

  const v12 = intersect(lines[0]!, lines[1]!);
  const v23 = intersect(lines[1]!, lines[2]!);
  const v31 = intersect(lines[2]!, lines[0]!);

  const valid = v12 !== null && v23 !== null && v31 !== null;
  const vs = (valid ? [v12, v23, v31] : []) as { x: number; y: number }[];

  const verticesXY = valid ? vs : [];
  const centroidXY = valid
    ? {
        x: (vs[0]!.x + vs[1]!.x + vs[2]!.x) / 3,
        y: (vs[0]!.y + vs[1]!.y + vs[2]!.y) / 3,
      }
    : { x: 0, y: 0 };

  // Convert xy back to lat/lon.
  const xyToLatLon = (p: { x: number; y: number }) => ({
    lonDeg: bearings[0]!.mark.lonDeg + p.x / (60 * cosRef),
    latDeg: bearings[0]!.mark.latDeg + p.y / 60,
  });

  const meanSideNm = valid
    ? (sideLen(vs[0]!, vs[1]!) +
        sideLen(vs[1]!, vs[2]!) +
        sideLen(vs[2]!, vs[0]!)) /
      3
    : Infinity;

  return {
    vertices: verticesXY.map(xyToLatLon),
    centroid: xyToLatLon(centroidXY),
    meanSideNm,
    valid,
  };
}

function sideLen(
  a: { x: number; y: number },
  b: { x: number; y: number },
): number {
  return Math.sqrt((a.x - b.x) ** 2 + (a.y - b.y) ** 2);
}

/// Plain-English quality tag for the cocked hat. Drives the
/// calculator's "Fix quality" pill.
export function fixQuality(meanSideNm: number): {
  label: string;
  rating: "excellent" | "good" | "acceptable" | "poor";
} {
  if (meanSideNm < 0.1)
    return { label: "Excellent (< 0.1 nm)", rating: "excellent" };
  if (meanSideNm < 0.2) return { label: "Good (< 0.2 nm)", rating: "good" };
  if (meanSideNm < 0.4)
    return { label: "Acceptable (< 0.4 nm)", rating: "acceptable" };
  return {
    label: "Poor — re-check bearings or pick different marks",
    rating: "poor",
  };
}
